import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as Papa from 'papaparse';
import { FileUpload } from 'primeng/fileupload';
import { AuthService } from 'src/app/auth.service';
import { BULK_PAY_APPROVALS, BULK_PAY_HISTORY } from 'src/app/routes';
import Utils from 'src/app/utils/utils';
import { environment } from 'src/environments/environment';
import { LoaderService } from '../../loader.service';
import { AlertDialogOptions, AlertDialogService } from '../../shared/alert-dialog/alert-dialog.service';
import { BulkPayArrayFields, BulkPayService, BulkPayTransactionConfig } from '../bulkpay.service';

@Component({
  selector: 'app-bulkpay-initiate',
  templateUrl: './initiate.component.html',
  styleUrls: ['./initiate.component.css']
})
export class BulkPayInitiateComponent implements OnInit {
  public validated: {
    totalAmount: number,
    transactionsCount: number;
  } = null;
  public validationError = false;
  public fileDetails: {
    records: any,
    name: string,
  } = null;
  private maxTransactions = environment.maxBulkPayTransactionsPerFile;
  private userAccess: string[] = [];
  @ViewChild('fileUpload') fileUploadRef: FileUpload;

  public enabledTransactionTypes: BulkPayTransactionConfig[] = [];

  bulkPayFormGroup = new FormGroup({
    subTransactionType: new FormControl(null, Validators.required),
    comment: new FormControl(null, Validators.required),
    remark: new FormControl(null, [Validators.required, Validators.minLength(1)]),
  });

  constructor(
    public bulkPayService: BulkPayService,
    private loaderService: LoaderService,
    private alertDialogService: AlertDialogService,
    private authService: AuthService,
    private router: Router) { }

  resetFile() {
    this.fileDetails = null;
    this.validated = null;
    this.validationError = false;
  };

  ngOnInit() {
    this.refreshEnabledTransactionTypes();

    this.authService.getUserDetails().subscribe((user: any) => {
      this.userAccess = (user && user.permissions) || [];
      this.refreshEnabledTransactionTypes();
    });

    this.resetOnchangeOf("subTransactionType", ["remark"], () => {
      this.fileUploadRef && this.fileUploadRef.clear();
      this.resetFile();
    });
  }

  selectedSubTransactionType() {
    return this.bulkPayFormGroup.get("subTransactionType").value;
  }

  allowsCustomRemark(): boolean {
    const type = this.selectedSubTransactionType();
    return !!(type && (!type.remarks || type.remarks.length === 0));
  }

  private refreshEnabledTransactionTypes() {
    this.enabledTransactionTypes = this.bulkPayService.transactionTypes
      .filter(t => !t.disabled && this.hasRequiredRole(t));

    const selected = this.selectedSubTransactionType();
    if (selected && !this.enabledTransactionTypes.some(t => t.name === selected.name)) {
      this.bulkPayFormGroup.get('subTransactionType').setValue(null);
    }
  }

  private hasRequiredRole(type: BulkPayTransactionConfig): boolean {
    // Legacy types omit `roles` and stay visible to everyone.
    // Types with a `roles` array (incl. empty) are shown only to matching users.
    // Values are permission keys (user.permissions).
    if (type.roles == null) {
      return true;
    }
    if (type.roles.length === 0) {
      return false;
    }
    return type.roles.some(role => this.userAccess.includes(role));
  }

  resetOnchangeOf(controlName: string, toReset: string[], cb = () => { }) {
    this.bulkPayFormGroup.get(controlName).valueChanges.subscribe(() => {
      toReset.forEach(control => {
        this.bulkPayFormGroup.get(control).setValue(null);
      });
      cb();
    });
  }

  handleFile(event: { files: File[]; }) {
    this.validated = null;
    const file = event.files[0];
    this.resetFile();
    Papa.parse(file, {
      complete: (results) => {
        this.fileDetails = { records: results.data, name: file.name };
      },
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      transform: (value, header: string) => this.parseCSVValue(value, header),
    });
  }

  parseCSVValue(value: string, header: string): any {
    if (BulkPayArrayFields.includes(header)) {
      return value.split("|")
        .map(v => v.trim());
    }

    return value.trim();
  }

  downloadSampleFile() {
    const subTransactionType = this.selectedSubTransactionType();
    if (!subTransactionType) {
      return;
    }
    Utils.downloadCSVFile({
      fields: subTransactionType.headers,
      data: subTransactionType.sampleData,
      fileName: `${subTransactionType.name.toLowerCase()}.csv`,
    });
  }

  showAlertBox(options: AlertDialogOptions, cb = () => { }) {
    this.alertDialogService.open(options);
    this.alertDialogService.confirmed()
      .subscribe(confirmed => confirmed && cb());
  }

  async validateBulkPayFile() {
    const valid = this.validateTransactionCount();
    if (!valid) {
      return;
    }

    this.loaderService.openLoading();
    try {
      this.validated = await this.bulkPayService
        .validateBulkPayFile(this.bulkPayRequest());

      this.showAlertBox({
        title: 'Success',
        message: 'File validation successful.',
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loaderService.closeLoading();
    }
  }

  validateTransactionCount() {
    const transactions = this.fileDetails.records;
    const count = transactions.length;
    if (count > this.maxTransactions) {
      this.showAlertBox({
        title: 'Error',
        message: `You can only upload ${this.maxTransactions} transactions at a time.`,
      });
      return false;
    }
    return true;
  }

  confirmBulkPayInitiate() {
    if (new Blob([JSON.stringify(this.bulkPayRequest())]).size > 12 << 20) {
      this.showAlertBox({
        title: 'Error',
        message: 'bulk pay file is too large to submit for approval',
      });
      return;
    }

    const subTransactionType = this.selectedSubTransactionType();
    let message = `You are about to credit ₹${this.validated.totalAmount} to ${this.validated.transactionsCount} captains. Do you want to proceed?`
    if (subTransactionType.type == "debit") {
      message = `You are about to debit ₹${Math.abs(this.validated.totalAmount)} from ${this.validated.transactionsCount} captains. Do you want to proceed?`
    }

    this.showAlertBox({
      title: 'Confirm',
      message,
      confirmText: 'Yes, proceed',
    }, () => this.initiateBulkPay());
  }

  initiateBulkPay() {
    this.loaderService.openLoading();
    this.bulkPayService
      .initiateBulkPay(this.bulkPayRequest())
      .then(res => {
        if (res && res.autoApproved === false) {
          this.showAlertBox({
            title: 'Submitted for approval',
            message: res.message || 'Bulk payment submitted for approval. It will run after a checker approves it.',
            confirmText: 'View approvals',
          }, () => this.router.navigateByUrl(BULK_PAY_APPROVALS));
          return;
        }
        this.showAlertBox({
          title: 'Success',
          message: 'Bulk pay initiated successfully.',
          confirmText: 'View history',
        }, () => this.gotoHistoryPage());
      })
      .catch(err => this.handleError(err))
      .finally(() => this.loaderService.closeLoading());
  }

  gotoHistoryPage(): void {
    this.router.navigateByUrl(BULK_PAY_HISTORY);
  }

  private bulkPayRequest(): any {
    const subTransactionType = this.selectedSubTransactionType();
    return {
      subTransactionType: subTransactionType.name,
      remarks: this.bulkPayFormGroup.get("remark").value,
      comment: this.bulkPayFormGroup.get("comment").value,
      transactions: this.fileDetails.records,
    };
  }

  private handleError(err: any) {
    const unknownErr = "Unknown error, Please check history to get latest status of your file.";
    const res = err.error || { message: unknownErr };
    if (!res.data) {
      this.showAlertBox({
        title: 'Error',
        message: res.message,
      });
      return;
    }

    const data = res.data;
    if (data.code && data.code == "BULK_PAY_VALIDATION_ERROR") {
      this.validationError = true;
      const errors = data.errors;
      const value = errors
        .map(err => `Record: ${err.recordNumber} - ${err.error}`);

      this.showAlertBox({
        title: 'Validation Error',
        details: [
          {
            value,
            message: "Please correct the errors and try again."
          }
        ],
      });
      return;
    }

    const error = data.error || data;

    this.showAlertBox({
      title: 'Error',
      message: error.message || unknownErr,
    });
  }
}
